import { useContext, useEffect, useState } from 'react';
import { UserProfileContext } from '../../../context/userProfileContext';
import {
    FamilyMemberType,
    Insurance,
    InsurancePolywizzErrorStatuses,
    InsurancePolywizzStatuses,
    Polywizz,
    SubmitPolywizzParams,
} from '../../../interfaces/Insurance';
import { formatDate } from '../../../utils/formatDate';
import { camelCaseToText } from '../../../utils/camelCaseToText';
import { getPolywizz, getPolywizzByInsuranceId, getPolywizzStatusTrackers } from '../../../utils/api/insurance';
import { Client } from '../../../interfaces/Client';
import ResubmitInsurnace from './insurance-resubmit';

interface PolywizzStatusTracker {
    _id: string;
    polywizz: string;
    client: string;
    changer: string;
    newStatus: string;
    prevStatus?: string;
    createdAt: Date;
}

const PolywizzStatusHistory = ({ polywizz }: { polywizz: Polywizz | null }) => {
    const { getUserById } = useContext(UserProfileContext);
    const [statusTrackers, setStatusTrackers] = useState<PolywizzStatusTracker[]>([]);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const fetchTrackers = async () => {
            if (!polywizz?._id) return;
            setLoading(true);
            try {
                const response = await getPolywizzStatusTrackers({ polywizzId: polywizz._id });
                setStatusTrackers(response?.statusTrackers || []);
            } catch (error) {
                console.error('Error fetching polywizz status trackers:', error);
                setStatusTrackers([]);
            } finally {
                setLoading(false);
            }
        };
        fetchTrackers();
    }, [polywizz?._id]);

    const handleCopyPolywizzId = () => {
        if (polywizz?._id) {
            navigator.clipboard.writeText(polywizz._id);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    if (!polywizz) return null;

    return (
        <div style={{ marginTop: '10px' }}>
            <h5
                style={{ textAlign: 'left', marginBottom: '8px', cursor: 'pointer' }}
                onClick={handleCopyPolywizzId}
                title="Click to copy Polywizz ID"
            >
                Polywizz Status History <i className="mdi mdi-content-copy" style={{ fontSize: '14px' }}></i>
                {copied && <span style={{ fontSize: '12px', color: 'green', marginLeft: '5px' }}>Copied!</span>}
            </h5>
            {loading ? (
                <div>Loading...</div>
            ) : statusTrackers.length > 0 ? (
                <table className="table table-striped table-bordered" style={{ fontSize: '12px' }}>
                    <thead>
                        <tr>
                            <th>Status</th>
                            <th>Time of Change</th>
                            <th>Changed by</th>
                        </tr>
                    </thead>
                    <tbody>
                        {statusTrackers.map((tracker, index) => (
                            <tr key={tracker._id || index}>
                                <td>{camelCaseToText(tracker.newStatus || '')}</td>
                                <td>{formatDate(tracker.createdAt)}</td>
                                <td>{tracker.changer ? getUserById(tracker.changer)?.fullName : ''}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div style={{ fontSize: '12px', color: '#666' }}>No status history available.</div>
            )}
        </div>
    );
};
const InsuranceFamilyMemebrs = () => {
    const { profileContextData } = useContext(UserProfileContext);
    const { client } = profileContextData;
    const [currentRound, setCurrentRound] = useState<Insurance>(profileContextData.currentRound as Insurance);
    const [polywizzClient, setPolywizzClient] = useState<Polywizz | null>(null);
    const [polywizzSpouse, setPolywizzSpouse] = useState<Polywizz | null>(null);

    useEffect(() => {
        setCurrentRound(profileContextData.currentRound as Insurance);
    }, [profileContextData.currentRound]);

    const handlePolywizzData = async () => {
        const { polywizzClient, polywizzSpouse } = await getPolywizzByInsuranceId(currentRound._id);

        setPolywizzClient(polywizzClient);
        setPolywizzSpouse(polywizzSpouse);
    };
    useEffect(() => {
        handlePolywizzData();
    }, []);

    const getStatusContent = (polywizz: Polywizz | null, familyMemberType?: string) => {
        const status = polywizz?.status;

        if (
            !status ||
            [
                InsurancePolywizzStatuses.failed,
                InsurancePolywizzStatuses.timeout,
                InsurancePolywizzStatuses.policiesError,
                InsurancePolywizzStatuses.completedPoliciesNotRequired,
            ].includes(status)
        ) {
            return <span className="d-inline-flex align-items-center gap-1">{status ? camelCaseToText(status) : 'Not started'}</span>;
        } else {
            return camelCaseToText(status);
        }
    };

    const showKitStatus = (familyMemberType: string) => {
        if (client && currentRound) {
            const data = getStatusContent(familyMemberType === FamilyMemberType.client ? polywizzClient : polywizzSpouse, familyMemberType);

            return (
                <div>
                    {data && (
                        <div className="">
                            <span className="fw-normal fs-5">{data}</span>
                        </div>
                    )}
                </div>
            );
        }
    };

    return (
        <div className="detailed-tab family-members">
            {currentRound.familyMembers.length ? (
                <>
                    <table>
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Full Name</th>
                                <th>Gender</th>
                                <th>ID Number</th>
                                <th>Date of Birth</th>
                                <th>ID Issue Date</th>
                                <th>POA Signed</th>
                                <th>Errors</th>
                                <th>Polywizz Status</th>
                                <th>Mountain Submission Date</th>
                                <th>Policies Submission Date</th>
                                <th>Failed Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentRound.familyMembers.map((member, index) => (
                                <tr key={index} className={member.errors.length ? 'errors' : ''}>
                                    <td>{camelCaseToText(member.familyMemberType)}</td>
                                    <td>
                                        {member.firstName} {member.lastName}
                                    </td>
                                    <td>{member.gender}</td>
                                    <td>{member.idNumber}</td>
                                    <td>{formatDate(member.dateOfBirth, true)}</td>
                                    <td>{formatDate(member.idIssueDate, true)}</td>
                                    <td>{member.isPoaSigned ? 'Yes' : 'No'}</td>
                                    <td>
                                        {member.errors.map((error: string, errorIndex: number) => (
                                            <div key={errorIndex}>
                                                <b>{camelCaseToText(error)}</b>
                                            </div>
                                        ))}
                                    </td>
                                    <td>{showKitStatus(member.familyMemberType)}</td>
                                    <td>
                                        <ResubmitInsurnace
                                            client={client}
                                            insuranceId={currentRound._id}
                                            polywizz={member.familyMemberType === FamilyMemberType.client ? polywizzClient : polywizzSpouse}
                                            familyMemberType={member.familyMemberType}
                                            isMountainDate={true}
                                        />
                                    </td>
                                    <td>
                                        <ResubmitInsurnace
                                            client={client}
                                            insuranceId={currentRound._id}
                                            polywizz={member.familyMemberType === FamilyMemberType.client ? polywizzClient : polywizzSpouse}
                                            familyMemberType={member.familyMemberType}
                                            isMountainDate={false}
                                        />
                                    </td>
                                    <td>
                                        {member.familyMemberType === FamilyMemberType.client && polywizzClient?.error ? polywizzClient?.error : ''}
                                        {member.familyMemberType === FamilyMemberType.spouse && polywizzSpouse?.error ? polywizzSpouse?.error : ''}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Polywizz Status History Tables - Client and Spouse side by side */}
                    {(polywizzClient || polywizzSpouse) && (
                        <div style={{ display: 'flex', gap: '20px', marginTop: '20px', width: '50%' }}>
                            {polywizzClient && (
                                <div style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                    <h5 style={{ marginBottom: '10px' }}>
                                        {currentRound.familyMembers.find((m) => m.familyMemberType === FamilyMemberType.client)?.firstName}{' '}
                                        {currentRound.familyMembers.find((m) => m.familyMemberType === FamilyMemberType.client)?.lastName} (Client)
                                    </h5>
                                    <PolywizzStatusHistory polywizz={polywizzClient} />
                                </div>
                            )}
                            {polywizzSpouse && (
                                <div style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                    <h5 style={{ marginBottom: '10px' }}>
                                        {currentRound.familyMembers.find((m) => m.familyMemberType === FamilyMemberType.spouse)?.firstName}{' '}
                                        {currentRound.familyMembers.find((m) => m.familyMemberType === FamilyMemberType.spouse)?.lastName} (Spouse)
                                        {polywizzSpouse.familyMemberType === FamilyMemberType.client && (
                                            <span style={{ fontSize: '12px', color: '#666', marginLeft: '5px' }}>- from spouse's profile</span>
                                        )}
                                    </h5>
                                    <PolywizzStatusHistory polywizz={polywizzSpouse} />
                                </div>
                            )}
                        </div>
                    )}
                </>
            ) : (
                <p className="fw-normal">No family members</p>
            )}
        </div>
    );
};

export default InsuranceFamilyMemebrs;
